import { memo, PropsWithChildren, ComponentProps, type JSX } from 'react';
import {
   PopoverRoot,
   PopoverPrimitiveTrigger,
   PopoverPortal,
   PopoverPrimitiveContent,
   PopoverArrow,
} from '@ifixit/ui/components/popover';
import { cn } from '@ifixit/ui/lib/utils';

type VerticalContentsWrapperProps = ComponentProps<'div'>;

function VerticalContentsWrapper({ className, ...props }: VerticalContentsWrapperProps) {
   return (
      <div
         className={cn(
            'flex flex-col items-start p-3 md:p-4 max-h-[50vh] overflow-y-auto overflow-x-hidden',
            className
         )}
         {...props}
      />
   );
}

type GeneralTooltipContentProps = {
   maxWidth?: string;
   className?: string;
   zIndex?: number;
};

export type GeneralTooltipProps = PropsWithChildren<{
   content: JSX.Element | string;
   contentProps?: GeneralTooltipContentProps;
   appendTo?: () => Element | null;
   isOpen?: boolean;
   defaultIsOpen?: boolean;
   onOpen?: () => void;
   onClose?: () => void;
   isLazy?: boolean;
   closeOnBlur?: boolean;
   autoFocus?: boolean;
   returnFocusOnClose?: boolean;
   closeOnEsc?: boolean;
}>;

const GeneralTooltip = ({
   children,
   content,
   contentProps,
   appendTo,
   isOpen,
   defaultIsOpen,
   onOpen,
   onClose,
   isLazy,
   closeOnBlur = true,
   autoFocus = true,
}: GeneralTooltipProps) => {
   const attachTo = appendTo ? appendTo() : null;

   const handleOpenChange = (open: boolean) => {
      if (open) {
         onOpen?.();
      } else {
         onClose?.();
      }
   };

   const { maxWidth, className: contentClassName, zIndex = 9999 } = contentProps || {};

   const tooltipContentElement = (
      <PopoverPrimitiveContent
         className={cn(
            'text-black bg-white rounded-md p-0 shadow-2xl w-auto z-[9999] animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            contentClassName
         )}
         style={{ maxWidth, zIndex }}
         sideOffset={5}
         onOpenAutoFocus={(e: Event) => {
            if (!autoFocus) e.preventDefault();
         }}
         onInteractOutside={(e: Event) => {
            if (!closeOnBlur) e.preventDefault();
         }}
      >
         {content}
         <PopoverArrow className="fill-white" />
      </PopoverPrimitiveContent>
   );

   // Don't render content if lazy and not open
   const shouldRenderContent = !isLazy || isOpen;

   return (
      <PopoverRoot open={isOpen} defaultOpen={defaultIsOpen} onOpenChange={handleOpenChange}>
         <PopoverPrimitiveTrigger asChild>{children}</PopoverPrimitiveTrigger>
         {shouldRenderContent && (
            <PopoverPortal container={attachTo}>{tooltipContentElement}</PopoverPortal>
         )}
      </PopoverRoot>
   );
};

export default memo(GeneralTooltip);
export { VerticalContentsWrapper };
